import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { Pagination, PAGINATION_PAGE_SIZE } from '@/components/Pagination';
import { Button } from '@/components/ui/button';
import { PermissionGuard } from '@/components/PermissionGuard';
import { partnerService } from '@/services/partner';
import { Partner, PartnerEmail } from '@/types/partner';
import { usePartnerClients } from './hooks/usePartnerClients';
import { ClientTableHeader } from './components/ClientTableHeader';
import { ClientTable } from './components/ClientTable';
import { AddClientModal } from './components/AddClientModal';
import { EditClientModal } from './components/EditClientModal';
import { DeleteClientModal } from './components/DeleteClientModal';
import { ResendInviteModal } from './components/ResendInviteModal';
import { InviteStillValidModal } from './components/InviteStillValidModal';
import { isInviteWithinValidity, getClientStatus } from './utils/clientDisplay';

export default function PartnerClientsPage() {
  const { partnerId = '' } = useParams<{ partnerId: string }>();
  const navigate = useNavigate();
  const [partner, setPartner] = useState<Partner | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<PartnerEmail | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isResendModalOpen, setIsResendModalOpen] = useState(false);
  const [isInviteValidModalOpen, setIsInviteValidModalOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (!partnerId) return;
    partnerService
      .getAll({ offset: 0, limit: 1000 })
      .then((response) => {
        const found = response.data.find((item) => item.id === partnerId) ?? null;
        setPartner(found);
      })
      .catch(() => setPartner(null));
  }, [partnerId]);

  const { clients, total, loading, error, refetch } = usePartnerClients({
    partnerId,
    offset: (currentPage - 1) * PAGINATION_PAGE_SIZE,
    limit: PAGINATION_PAGE_SIZE,
    q: debouncedSearchTerm || undefined,
  });

  const handleResend = (client: PartnerEmail) => {
    if (getClientStatus(client) === 'ACTIVE') {
      return;
    }
    setSelectedClient(client);
    if (isInviteWithinValidity(client) && getClientStatus(client) === 'PENDING') {
      setIsInviteValidModalOpen(true);
      return;
    }
    setIsResendModalOpen(true);
  };

  const handleEdit = (client: PartnerEmail) => {
    setSelectedClient(client);
    setIsEditModalOpen(true);
  };

  const handleDelete = (client: PartnerEmail) => {
    setSelectedClient(client);
    setIsDeleteModalOpen(true);
  };

  const closeModals = () => {
    setSelectedClient(null);
    setIsEditModalOpen(false);
    setIsDeleteModalOpen(false);
    setIsResendModalOpen(false);
    setIsInviteValidModalOpen(false);
  };

  if (!partnerId) {
    navigate('/app/settings/partners');
    return null;
  }

  return (
    <div className="w-full">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <ClientTableHeader
          partnerName={partner?.name ?? 'Marca'}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
        />
        <PermissionGuard action="edit_partners">
          <Button
            type="button"
            className="flex items-center gap-2 bg-black text-white hover:bg-gray-900 shrink-0"
            onClick={() => setIsAddModalOpen(true)}
          >
            <Plus className="w-4 h-4" />
            Novo cliente
          </Button>
        </PermissionGuard>
      </div>

      <div className="rounded-lg overflow-hidden w-full mt-12 mb-6">
        <ClientTable
          clients={clients}
          isLoading={loading}
          error={error}
          onResend={handleResend}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </div>

      {!loading && total > 0 && (
        <Pagination
          currentPage={currentPage}
          totalItems={total}
          onPageChange={setCurrentPage}
        />
      )}

      <AddClientModal
        isOpen={isAddModalOpen}
        partnerId={partnerId}
        partnerName={partner?.name ?? 'Marca'}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={refetch}
      />
      <EditClientModal
        isOpen={isEditModalOpen}
        partnerId={partnerId}
        client={selectedClient}
        onClose={closeModals}
        onSuccess={refetch}
      />
      <DeleteClientModal
        isOpen={isDeleteModalOpen}
        client={selectedClient}
        partnerId={partnerId}
        onClose={closeModals}
        onSuccess={refetch}
      />
      <ResendInviteModal
        isOpen={isResendModalOpen}
        client={selectedClient}
        onClose={closeModals}
        onSuccess={refetch}
      />
      <InviteStillValidModal
        isOpen={isInviteValidModalOpen}
        client={selectedClient}
        onClose={closeModals}
      />
    </div>
  );
}
